import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { LoginService } from '../login/login.service';
import { Router } from '@angular/router';
import { InsuranceInfo } from 'projects/shared/src/public-api';
import { environment } from 'projects/shared/src/environments/environment';
import { ApiService } from '../api-service/api.service';

@Injectable({
  providedIn: 'root',
})
export class InsuranceInfoService {
  constructor(
    private router: Router,
    private apiService: ApiService,
    private loginService: LoginService
  ) {}

  /**
   * Retrieves insurance prices for the provided insurance information, with authentication.
   *
   * @param {InsuranceInfo} insuranceInfo - The insurance request details.
   * @returns {Observable<any>} - An Observable emitting the insurance prices or an error.
   * The function checks for a valid access token and includes it in the request headers.
   * If the token is expired, it refreshes the token or redirects the user to the login page.
   */
  getInsurancePrices(
    insuranceInfo: InsuranceInfo,
    requireAuth: boolean = true
  ): Observable<any> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    const params = new HttpParams({
      fromObject: insuranceInfo as any,
    });

    if (requireAuth) {
      const accessToken = this.loginService.getActiveToken();
      const refreshToken = this.loginService.getRefreshToken();

      if (!accessToken || !refreshToken) {
        console.error('Error with tokens in getInsurancePrices');
        this.loginService.clearTokens();
        this.router.navigate(['/login']);
        return of(null);
      }

      const isExpired = this.loginService.isTokenExpired(accessToken);

      if (isExpired) {
        return this.loginService.refreshAccessToken(refreshToken).pipe(
          switchMap(() => {
            const refreshedAccessToken = this.loginService.getActiveToken();
            headers = headers.set(
              'Authorization',
              `Bearer ${refreshedAccessToken}`
            );

            return this.apiService.makeApiCall<any>(
              `${environment.apiGetInsurancePrices}?${params.toString()}`,
              headers,
              'GET'
            );
          }),
          catchError((error) => {
            console.error('Error refreshing token', error);
            this.loginService.clearTokens();
            this.router.navigate(['/login']);
            return throwError(() => error);
          })
        );
      }

      // If the token is not expired, proceed with the API call
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    // Proceed with the API call directly if auth is not required
    return this.apiService.makeApiCall<any>(
      `${environment.apiGetInsurancePrices}?${params.toString()}`,
      headers,
      'GET'
    );
  }
}
